import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BrowserControlCommunicationService, GmTireCentralService } from '@applications/sha-services';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { QuoteDetailQueryParameters } from '@quorum/models/xs-query';
import { ServiceAppointment as Appointment, Vehicle, WorkOrder, WorkOrderCase } from '@quorum/models/xs-resource';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { MediaStateService } from '@quorum/sha-media';
import { InspectionService } from '@quorum/xsr-legacy-vip/services';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'svc-work-order-inspection-shell',
  templateUrl: './work-order-inspection-shell.component.html',
  styleUrls: ['./work-order-inspection-shell.component.scss'],
})
export class WorkOrderInspectionShellComponent implements OnInit {
  appointmentWorkOrder$: Observable<{
    appointment: Appointment;
    workOrder: WorkOrder;
  }>;
  cases: WorkOrderCase[];
  mediaParams$: Observable<any>;
  activeTab = 0;
  lineItemsCollapsed = false;
  toggleTireFinderWidget = false;

  vehicle$: Observable<Vehicle | null>;

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    public inspectionService: InspectionService,
    public mediaStateService: MediaStateService,
    public browserControlService: BrowserControlCommunicationService,
    private serviceLaneFacade: ServiceLaneFacade,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService,
    private gmTireCentralService: GmTireCentralService
  ) {}

  ngOnInit(): void {
    const workOrderIdParam = this.route.snapshot.queryParams.workOrderId;
    if (!workOrderIdParam) return;

    const workOrderId = parseInt(workOrderIdParam, 10);

    this.inspectionService.triggerInspectionLogic.next(workOrderId);

    this.inspectionService
      .getQuote(workOrderIdParam)
      .then((quote) => {
        if (quote?.VehicleId) {
          this.mediaStateService.loadMediaForVehicle(quote.VehicleId);
        }
      })
      .catch(() => {
        // swallow to avoid breaking page load if quote fails
      });

    this.appointmentWorkOrder$ = this.apiService
      .get(`v/1/fixed-operations/work-orders/${workOrderId}`, {
        params: {
          embed: 'appointment,vehicle,customerAssociate,serviceAdvisorAssociate,employee',
        },
      })
      .pipe(
        take(1),
        switchMap((workOrder) => {
          const associateId = workOrder.customerId;
          const addresses$ = associateId
            ? this.apiService.get(`v/1/associates/associates/${associateId}/addresses`)
            : of([]);

          this.vehicle$ = workOrder.embedded?.vehicle
            ? of(workOrder.embedded.vehicle)
            : workOrder.vehicleId
            ? this.apiService.get<Vehicle>(`v/1/vehicles/vehicles/${workOrder.vehicleId}`)
            : of(null);

          const appointment$ = workOrder.appointmentId
            ? this.apiService
                .get(`v/1/fixed-operations/appointments`, {
                  params: {
                    id: workOrder.appointmentId,
                    embed: 'serviceCode,serviceAdvisorAssociate,transportOption,customer',
                  },
                })
                .pipe(map((appts) => appts[0]))
            : of(null);

          return combineLatest([
            appointment$,
            addresses$,
            this.vehicle$.pipe(take(1)),
            this.serviceLaneFacade.dealerBac$.pipe(
              filter((dealerBac) => dealerBac !== null),
              take(1)
            ),
            this.serviceLaneFacade.oem$.pipe(
              filter((oem) => oem !== null),
              take(1),
              map((oem) => oem.associateId)
            ),
            this.authenticationStateService.selectAuthenticatedEmployee().pipe(
              filter((employee) => employee != null),
              take(1),
              switchMap((employee) => {
                return this.systemControlStateService.getSystemControlValues(
                  ['CL_GM_TIRE_CENTRAL_WIDGET', 'CL_DEALER_COUNTRY_ID'],
                  employee.rooftopId
                );
              }),
              take(1)
            ),
          ]).pipe(
            tap(
              ([appointment, addresses, vehicle, dealerBac, oem, systemControls]: [
                Appointment | null,
                any[],
                Vehicle | null,
                string,
                string,
                Record<string, any>
              ]) => {
                const dealerCountryCode =
                  systemControls['CL_DEALER_COUNTRY_ID'].value === 'CAN'
                    ? 'CA'
                    : systemControls['CL_DEALER_COUNTRY_ID'].value === 'USA'
                    ? 'US'
                    : 'US';

                if (vehicle && oem === 'GM' && systemControls?.CL_GM_TIRE_CENTRAL_WIDGET?.value === '1') {
                  this.gmTireCentralService.setupGMTireCentralIframe(
                    vehicle,
                    workOrderId,
                    dealerBac,
                    dealerCountryCode
                  );
                }
              }
            ),
            map(([appointment, addresses, vehicle, dealerBac, oem, systemControls]) => {
              const workOrderWithVehicle = vehicle
                ? {
                    ...workOrder,
                    embedded: {
                      ...(workOrder.embedded ?? {}),
                      vehicle,
                    },
                  }
                : {
                    ...workOrder,
                    embedded: workOrder.embedded ?? {},
                  };

              return {
                appointment,
                customerAddress: addresses[0],
                workOrder: workOrderWithVehicle,
                vehicle,
                dealerBac,
                oem,
                systemControls,
              };
            })
          );
        }),
        shareReplay(1)
      );

    this.mediaParams$ = this.mediaStateService.getMediaParamsForKey('workOrderCase');
    this.apiService.get(`/v/1/fixed-operations/work-orders/${workOrderId}/cases`).subscribe((cases) => {
      this.cases = cases;
    });
  }

  handleBodyReportSignatureCompleted(signature: any) {
    this.inspectionService.bodyReportObject.signature = signature;
  }

  toggleTireFinder(toggle: boolean) {
    this.toggleTireFinderWidget = toggle;
    this.refreshQuoteLines();
  }

  refreshQuoteLines() {
    this.inspectionService.quoteId$
      .pipe(
        filter((quoteId): quoteId is string => !!quoteId),
        take(1)
      )
      .subscribe((quoteId) => {
        this.serviceLaneFacade.getQuoteLinesFromServer(
          new QuoteDetailQueryParameters({ quoteHeaderId: quoteId, embed: 'employeeAssociate,serviceGroup' })
        );
      });
  }

  hideTireFinderIfEnabled() {
    if (this.toggleTireFinderWidget) {
      this.toggleTireFinderWidget = false;
    }
  }
}
